import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, type IntlShape } from 'react-intl';
import { Badge, Button } from 'reactstrap';
import { getUnattributedPerson } from 'utils/models/Person';
import { stripHtml } from 'utils/util/util';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import Avatar from 'views/Widgets/People/Avatar';
import RelativeTime from 'views/Widgets/RelativeTime';
import {
  type CalibrationComment,
  CalibrationCommentVisibilty,
  type ThreadedCalibrationComment,
} from '../types';
import { getRecommendationDetails } from '../utils/comments';

const HIGHLIGHT_DURATION_MS = 1500;
const HIGHLIGHT_FADE_MS = 700;

export type CommentItemProps = {
  comment: ThreadedCalibrationComment;
  depth: number;
  isReplying: boolean;
  onReply?: (comment: CalibrationComment) => void;
  onToggleThread?: (threadKey: string) => void;
  onToggleVisibility?: (comment: CalibrationComment) => void;
  isThreadExpanded?: boolean;
  repliesAvailable?: number;
  rootThreadKey?: string;
  formatMessage: IntlShape['formatMessage'];
  variant?: 'popover' | 'modal';
  canToggleVisibility?: boolean;
  onEdit?: (comment: CalibrationComment) => void;
  // Shown in place of the message and actions while the comment is being edited
  editForm?: React.ReactNode;
  isHighlighted?: boolean;
};

export const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  depth,
  isReplying,
  onReply,
  onToggleThread,
  onToggleVisibility,
  isThreadExpanded = false,
  repliesAvailable = 0,
  rootThreadKey = '',
  formatMessage,
  variant = 'modal',
  canToggleVisibility = false,
  onEdit,
  editForm,
  isHighlighted = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHighlightVisible, setIsHighlightVisible] = useState(isHighlighted);

  useEffect(() => {
    if (!isHighlighted) {
      return;
    }
    containerRef.current?.scrollIntoView({ block: 'center' });
    setIsHighlightVisible(true);
    const timeout = setTimeout(
      () => setIsHighlightVisible(false),
      HIGHLIGHT_DURATION_MS
    );
    return () => clearTimeout(timeout);
  }, [isHighlighted]);

  const isPopover = variant === 'popover';
  const commentId = String(comment.id);
  const commentDate = comment.created_at ? new Date(comment.created_at) : null;
  const hasValidDate = commentDate && !Number.isNaN(commentDate.getTime());
  const absoluteLabel = hasValidDate ? commentDate.toISOString() : '';
  const commentBody = comment.comment ?? '';
  const hasCommentContent = stripHtml(commentBody, true).trim().length > 0;
  const hasNoParent = !comment.parent_comment_id;
  const hasAuthor = !!comment.author;
  const recommendationDetails = getRecommendationDetails(
    formatMessage,
    comment.recommendation
  );
  const commentRecommendationTag =
    hasNoParent && comment.recommendation ? (
      <Badge color="primary" className="text-uppercase small fw-semibold">
        {recommendationDetails.label}
      </Badge>
    ) : null;

  const isRootComment = depth === 0;

  const containerClasses = [
    'calibration-thread-item',
    depth === 0
      ? 'calibration-thread-item--root'
      : 'calibration-thread-item--reply',
    isReplying ? 'calibration-thread-item--active' : '',
    // padding stays after the fade so the comment doesn't shift
    isHighlighted ? 'rounded p-2' : '',
    isHighlightVisible ? 'bg-primary-soft' : '',
    'd-flex',
    'gap-3',
  ]
    .filter(Boolean)
    .join(' ');

  const authorName =
    comment.author?.full_name ||
    formatMessage({
      id: 'app.views.widgets.dashboards.calibration_dashboard.comments.author.unknown',
      defaultMessage: 'Someone',
    });

  return (
    <div
      key={`calibration-comment-${commentId}`}
      ref={containerRef}
      className={containerClasses}
      style={
        isHighlighted
          ? { transition: `background-color ${HIGHLIGHT_FADE_MS}ms ease-out` }
          : undefined
      }
    >
      <Avatar
        person={
          hasAuthor
            ? comment.author!
            : getUnattributedPerson(formatMessage, 0, null, true)
        }
        size="sm"
        className="flex-shrink-0"
      />

      <div className="flex-grow-1">
        {isPopover ? (
          <div className="calibration-thread-metadata">
            <div className="fw-semibold">{authorName}</div>
            <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
              {hasValidDate && (
                <span className="text-muted small" title={absoluteLabel}>
                  <RelativeTime datetime={comment.created_at} />
                </span>
              )}
              {comment.visibility ===
                CalibrationCommentVisibilty.AUTHOR_AND_ADMINISTRATORS && (
                <Badge color="secondary" className="text-uppercase">
                  <FormattedMessage
                    id="app.views.widgets.dashboards.calibration_dashboard.comments.private_badge"
                    defaultMessage="Private"
                  />
                </Badge>
              )}
              {commentRecommendationTag}
            </div>
          </div>
        ) : (
          <div className="calibration-thread-metadata d-flex align-items-start justify-content-between gap-2 flex-wrap">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="fw-semibold">{authorName}</span>
              {hasValidDate && (
                <span className="text-muted small" title={absoluteLabel}>
                  <RelativeTime datetime={comment.created_at} />
                </span>
              )}
              {comment.visibility ===
                CalibrationCommentVisibilty.AUTHOR_AND_ADMINISTRATORS && (
                <Badge color="secondary" className="text-uppercase">
                  <FormattedMessage
                    id="app.views.widgets.dashboards.calibration_dashboard.comments.private_badge"
                    defaultMessage="Private"
                  />
                </Badge>
              )}
            </div>
            {commentRecommendationTag}
          </div>
        )}
        {editForm ?? (
          <>
            <div className="calibration-thread-message mt-2 text-body">
              {hasCommentContent ? (
                <div className="mb-0 text-body">
                  <RichTextViewer model={commentBody} />
                </div>
              ) : hasAuthor ? (
                <span className="fst-italic text-muted">
                  <FormattedMessage
                    id="app.views.widgets.dashboards.calibration_dashboard.comments.no_text"
                    defaultMessage="No additional details provided."
                  />
                </span>
              ) : (
                <span className="fst-italic text-muted">
                  <FormattedMessage
                    id="app.views.widgets.dashboards.calibration_dashboard.comments.private"
                    defaultMessage="This comment was made private."
                  />
                </span>
              )}
            </div>
            {(!isPopover || (isRootComment && repliesAvailable > 0)) && (
              <div className="calibration-thread-actions d-flex flex-wrap align-items-center gap-3 mt-2">
                {onReply && (
                  <Button
                    color="link"
                    size="sm"
                    className="px-0"
                    onClick={() => onReply(comment)}
                  >
                    <FormattedMessage
                      id="app.views.widgets.dashboards.calibration_dashboard.comments.reply"
                      defaultMessage="Reply"
                    />
                  </Button>
                )}
                {!isPopover && onEdit && (
                  <Button
                    color="link"
                    size="sm"
                    className="px-0"
                    onClick={() => onEdit(comment)}
                  >
                    <FormattedMessage
                      id="app.views.widgets.dashboards.calibration_dashboard.comments.edit"
                      defaultMessage="Edit"
                    />
                  </Button>
                )}
                {!isPopover && canToggleVisibility && onToggleVisibility && (
                  <Button
                    color="link"
                    size="sm"
                    className="px-0"
                    onClick={() => onToggleVisibility(comment)}
                  >
                    {comment.visibility ===
                    CalibrationCommentVisibilty.AUTHOR_AND_ADMINISTRATORS ? (
                      <FormattedMessage
                        id="app.views.widgets.dashboards.calibration_dashboard.comments.make_public"
                        defaultMessage="Make public"
                      />
                    ) : (
                      <FormattedMessage
                        id="app.views.widgets.dashboards.calibration_dashboard.comments.make_private"
                        defaultMessage="Make private"
                      />
                    )}
                  </Button>
                )}
                {isRootComment && repliesAvailable > 0 && onToggleThread && (
                  <Button
                    color="link"
                    size="sm"
                    className="px-0"
                    onClick={() => onToggleThread(rootThreadKey)}
                  >
                    {isThreadExpanded ? (
                      <FormattedMessage
                        id="app.views.widgets.dashboards.calibration_dashboard.comments.hide_replies"
                        defaultMessage="Hide replies"
                      />
                    ) : (
                      <FormattedMessage
                        id="app.views.widgets.dashboards.calibration_dashboard.comments.view_replies"
                        defaultMessage="View {count, number} {count, plural, one {reply} other {replies}}"
                        values={{ count: repliesAvailable }}
                      />
                    )}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
